import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@futuredev/shared-auth';
import { useTheme } from '../contexts/ThemeContext';
import api from '@futuredev/shared-auth/api';
import ThemeToggle from './ThemeToggle';
import * as HeroIcons from '@heroicons/react/24/outline';
import {
  CATEGORY_INFO,
  findCategoryForPath,
  getCategorySidebarItems,
  isCategoryLinkActive,
} from '../data/adminCardData';

interface NavItem {
  id: string;
  label: string;
  route?: string;
  icon_name?: string;
  iconName?: string;
  icon?: string;
  badge_text?: string;
  badge_color?: string;
  external_link?: boolean;
  children?: NavItem[];
}

const DEFAULT_NAV_ITEMS: NavItem[] = [
  { id: 'default-home', label: 'Home', route: '/', icon_name: 'HomeIcon' },
  { id: 'default-messaging', label: 'Messaging', route: '/admin/chat-management', icon_name: 'ChatBubbleLeftRightIcon' },
  { id: 'default-memberships', label: 'Memberships', route: '/membership', icon_name: 'UserGroupIcon' },
  { id: 'default-back-office', label: 'Back Office', route: '/admin', icon_name: 'BuildingOfficeIcon' },
  { id: 'default-dashboard', label: 'Dashboard', route: '/dashboard', icon_name: 'Squares2X2Icon' },
  { id: 'default-compliance', label: 'Compliance', route: '/admin/compliance', icon_name: 'ShieldCheckIcon' },
];

function getNavbarIconKey(item: NavItem): string | null {
  const raw = item?.icon_name ?? item?.iconName ?? item?.icon ?? null;
  if (typeof raw !== 'string') return null;
  const target = raw.trim().replace(/^HeroIcons\./, '').toLowerCase();
  return Object.keys(HeroIcons).find(key => key.toLowerCase() === target) || null;
}

function AdminNavbar() {
  const { user, logout, loading: authLoading } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [logoUrl, setLogoUrl] = useState('/rfctr-icon03.png');
  const categoryKey = findCategoryForPath(location.pathname);
  const sectionItems = useMemo(() => (categoryKey ? getCategorySidebarItems(categoryKey) : []), [categoryKey]);
  const sectionName = categoryKey ? CATEGORY_INFO[categoryKey]?.name ?? 'Other' : null;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    const loadNavbarItems = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await api.get('/navbar/admin');
        const data = response.data?.data;
        if (response.data?.success && Array.isArray(data) && data.length > 0) {
          setNavItems(data);
          if (response.data?.navbar_type?.logo_url) {
            setLogoUrl(response.data.navbar_type.logo_url);
          }
        } else {
          setNavItems(DEFAULT_NAV_ITEMS);
        }
      } catch (err: unknown) {
        console.error('Navbar load failed:', err);
        setNavItems(DEFAULT_NAV_ITEMS);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      loadNavbarItems();
    }
  }, [authLoading]);

  const toggleExpanded = (itemId: string): void => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const renderNavItem = (item: NavItem, isMobile = false): React.ReactNode => {
    const iconKey = getNavbarIconKey(item);
    const IconComponent = iconKey ? (HeroIcons as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>)[iconKey] : null;
    const ResolvedIcon = IconComponent || (iconKey ? HeroIcons.QuestionMarkCircleIcon : null);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);

    const navItemClass = `px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-1 ${isDark
        ? 'text-slate-300 hover:text-cyan-400 hover:bg-slate-800'
        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
      } ${isMobile ? 'space-x-2' : ''}`;

    if (hasChildren && !isMobile) {
      return (
        <div key={item.id} className="relative group">
          <button
            onClick={() => toggleExpanded(item.id)}
            className={navItemClass}
          >
            {ResolvedIcon && <ResolvedIcon className="w-5 h-5" />}
            <span className="whitespace-nowrap">{item.label}</span>
            {item.badge_text && (
              <span className={`text-xs px-1.5 py-0.5 rounded bg-${item.badge_color}-100 text-${item.badge_color}-800`}>
                {item.badge_text}
              </span>
            )}
            <HeroIcons.ChevronDownIcon className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
          {isExpanded && (
            <div className={`absolute top-full left-0 mt-1 w-48 rounded-lg shadow-lg border py-1 z-50 ${isDark
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
              }`}>
              {(item.children ?? []).map(child => renderNavItem(child, false))}
            </div>
          )}
        </div>
      );
    }

    const linkContent = (
      <>
        {ResolvedIcon && <ResolvedIcon className="w-5 h-5" />}
        <span className={isMobile ? '' : 'whitespace-nowrap'}>{item.label}</span>
        {item.badge_text && (
          <span className={`text-xs px-1.5 py-0.5 rounded bg-${item.badge_color}-100 text-${item.badge_color}-800`}>
            {item.badge_text}
          </span>
        )}
        {hasChildren && isMobile && (
          <HeroIcons.ChevronDownIcon className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        )}
      </>
    );

    if (item.external_link && item.route) {
      return (
        <a
          key={item.id}
          href={item.route}
          target="_blank"
          rel="noopener noreferrer"
          className={navItemClass}
          onClick={() => isMobile && setMobileMenuOpen(false)}
        >
          {linkContent}
        </a>
      );
    }

    if (item.route) {
      return (
        <Link
          key={item.id}
          to={item.route}
          className={navItemClass}
          onClick={() => {
            if (isMobile) {
              setMobileMenuOpen(false);
              if (hasChildren) {
                toggleExpanded(item.id);
              }
            }
          }}
        >
          {linkContent}
        </Link>
      );
    }

    return (
      <div key={item.id} className={`px-4 py-2 text-sm font-medium ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
        {item.label}
      </div>
    );
  };

  return (
    <nav className={`shadow-sm border-b md:sticky md:top-0 md:z-50 transition-colors duration-300 ${isDark
        ? 'bg-slate-900/95 backdrop-blur-sm border-slate-700/50'
        : 'bg-white border-gray-200'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity" onClick={() => setMobileMenuOpen(false)}>
              <img
                src={logoUrl}
                alt="Logo"
                className="h-8 w-auto object-contain"
                style={{
                  height: '32px',
                  width: 'auto',
                  display: 'block',
                  minWidth: '32px'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  console.error('Logo load failed:', target.src);
                  if (target.src !== '/rfctr-icon03.png') {
                    target.src = '/rfctr-icon03.png';
                  }
                }}
                onLoad={(e) => {
                  (e.target as HTMLImageElement).style.display = 'block';
                }}
              />
            </Link>
          </div>

          {!loading && (
            <div className="hidden md:flex items-center space-x-4">
              {navItems.map((item) => renderNavItem(item, false))}
              <ThemeToggle className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} />
              {user && (
                <button
                  onClick={handleLogout}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-1 ${isDark
                      ? 'text-slate-300 hover:text-red-400 hover:bg-slate-800'
                      : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                    }`}
                  title="Logout"
                >
                  <HeroIcons.ArrowRightOnRectangleIcon className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              )}
            </div>
          )}

          <div className="md:hidden flex items-center space-x-2">
            <ThemeToggle className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="admin-mobile-menu"
              className={`flex h-11 w-11 items-center justify-center rounded-md transition-colors ${isDark
                  ? 'text-slate-300 hover:text-cyan-400 hover:bg-slate-800'
                  : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                }`}
            >
              {mobileMenuOpen ? (
                <HeroIcons.XMarkIcon className="h-6 w-6" />
              ) : (
                <HeroIcons.Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && !loading && (
          <div
            id="admin-mobile-menu"
            className={`md:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto border-t py-4 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}
          >
            {sectionName && sectionItems.length > 0 && (
              <nav aria-label={`${sectionName} section`} className={`mb-4 border-b pb-4 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <h2 className={`px-4 pb-2 text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {sectionName}
                </h2>
                <ul className="flex flex-col gap-0.5">
                  {sectionItems.map((item) => {
                    const active = isCategoryLinkActive(item.link, location.pathname, location.search);
                    const Icon = item.icon as React.ComponentType<{ className?: string }>;
                    return (
                      <li key={item.name}>
                        <NavLink
                          to={item.link}
                          aria-current={active ? 'page' : undefined}
                          className={`flex min-h-[44px] items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium ${active
                              ? isDark ? 'bg-indigo-500/25 text-slate-100' : 'bg-indigo-500/10 text-gray-900'
                              : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          <span className={`${item.color} flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-white`}>
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="truncate">{item.name}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            )}
            <div className="flex flex-col space-y-2">
              {navItems.map((item) => {
                const rendered = renderNavItem(item, true);
                const hasChildren = item.children && item.children.length > 0;
                return (
                  <div key={item.id}>
                    {rendered}
                    {hasChildren && expandedItems.has(item.id) && (
                      <div className="ml-4 mt-1 space-y-1">
                        {(item.children ?? []).map(child => renderNavItem(child, true))}
                      </div>
                    )}
                  </div>
                );
              })}
              {user && (
                <button
                  onClick={handleLogout}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 ${isDark
                      ? 'text-slate-300 hover:text-red-400 hover:bg-slate-800'
                      : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                    }`}
                >
                  <HeroIcons.ArrowRightOnRectangleIcon className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default AdminNavbar;
